<script setup lang="ts">
const props = defineProps<{
  promocode?: string;
}>();

const promocodeStore = usePromocodeStore();

const promocode = ref(props.promocode ?? '');

watch(
  () => props.promocode,
  (val) => {
    if (val) promocode.value = val;
  },
);

const isPromocodeButtonActive = computed(
  () => promocode.value.trim().length > 0 && !promocodeStore.isRedeeming,
);

async function applyPromocode() {
  const success = await promocodeStore.redeemPromocode(promocode.value);

  if (success) promocode.value = '';
}

const bandBgUrl = getAssetsUrlForCDN(
  '/assets/images/perfil-oficial/copa/union.png',
);

const wrapperStyle = computed(() => ({
  '--band-image': `url('${bandBgUrl}')`,
}));

const teamLogosLeft = [
  '/assets/images/perfil-oficial/copa/teams/team-01.png',
  '/assets/images/perfil-oficial/copa/teams/team-02.png',
  '/assets/images/perfil-oficial/copa/teams/team-03.png',
  '/assets/images/perfil-oficial/copa/teams/team-04.png',
];

const teamLogosRight = [
  '/assets/images/perfil-oficial/copa/teams/team-05.png',
  '/assets/images/perfil-oficial/copa/teams/team-06.png',
  '/assets/images/perfil-oficial/copa/teams/team-07.png',
  '/assets/images/perfil-oficial/copa/teams/team-08.png',
];

const wrapperRef = ref<HTMLElement | null>(null);
const bandRef = ref<HTMLElement | null>(null);
const bandSize = ref({ w: 0, h: 0 });
const clipId = 'highlightedGamesBarClip';

const clipPathD = computed(() => {
  const W = bandSize.value.w;
  const H = bandSize.value.h;

  if (!W || !H || !wrapperRef.value) return '';

  const cs = getComputedStyle(wrapperRef.value);
  const notchW = parseFloat(cs.getPropertyValue('--notch-width')) || 0;
  const notchH = parseFloat(cs.getPropertyValue('--notch-height')) || 0;
  const slope = parseFloat(cs.getPropertyValue('--notch-slope')) || 0;
  const cornerR = parseFloat(cs.getPropertyValue('--notch-corner-radius')) || 6;

  if (!notchW || !notchH) {
    return `M 0 0 L ${W} 0 L ${W} ${H} L 0 ${H} Z`;
  }

  const slopeW = notchW * slope;
  const slopeLen = Math.hypot(slopeW, notchH) || 1;
  const denom = slopeLen - slopeW || 1;
  const maxR = Math.min(
    ((notchW / 2 - slopeW) * notchH) / denom,
    (slopeLen * notchH) / denom,
  );

  const r = Math.max(0, Math.min(cornerR, maxR));
  const d = (r * denom) / notchH;
  const dx = (d * slopeW) / slopeLen;
  const dy = (d * notchH) / slopeLen;
  const cx = W / 2;
  const LxTop = cx - notchW / 2 + slopeW;
  const RxTop = cx + notchW / 2 - slopeW;
  const LxBot = cx - notchW / 2;
  const RxBot = cx + notchW / 2;

  return [
    `M 0 ${notchH}`,
    `L ${LxBot} ${notchH}`,
    `L ${LxTop - dx} ${dy}`,
    `A ${r} ${r} 0 0 1 ${LxTop + d} 0`,
    `L ${RxTop - d} 0`,
    `A ${r} ${r} 0 0 1 ${RxTop + dx} ${dy}`,
    `L ${RxBot} ${notchH}`,
    `L ${W} ${notchH}`,
    `L ${W} ${H}`,
    `L 0 ${H}`,
    `Z`,
  ].join(' ');
});

let resizeObserver: ResizeObserver | null = null;

onMounted(() => {
  if (typeof ResizeObserver === 'undefined' || !bandRef.value) return;
  resizeObserver = new ResizeObserver((entries) => {
    const entry = entries[0];

    if (!entry) return;
    bandSize.value = {
      w: entry.contentRect.width,
      h: entry.contentRect.height,
    };
  });
  resizeObserver.observe(bandRef.value);
});

onBeforeUnmount(() => {
  resizeObserver?.disconnect();
  resizeObserver = null;
});
</script>

<template>
  <section
    ref="wrapperRef"
    :class="$style.wrapper"
    :style="wrapperStyle"
  >
    <svg
      aria-hidden="true"
      :class="$style.clipSvg"
      focusable="false"
      height="0"
      width="0"
    >
      <defs>
        <clipPath
          :id="clipId"
          clipPathUnits="userSpaceOnUse"
        >
          <path :d="clipPathD" />
        </clipPath>
      </defs>
    </svg>

    <div
      ref="bandRef"
      :class="$style.bandGradient"
      :style="{ clipPath: `url(#${clipId})` }"
    ></div>
    <div
      :class="$style.bandImage"
      :style="{ clipPath: `url(#${clipId})` }"
    ></div>

    <div :class="$style.titlePill">
      <div :class="$style.titlePillContent">
        <img
          alt=""
          :class="$style.titleAvatar"
          :src="
            getAssetsUrlForCDN(
              '/assets/images/perfil-oficial/esportes-pagina.png',
            )
          "
        />
        <img
          alt="Jogos em Destaque"
          :class="$style.titleImage"
          :src="
            getAssetsUrlForCDN(
              '/assets/images/perfil-oficial/copa/jogos-em-destaque.png',
            )
          "
        />
        <img
          alt=""
          :class="$style.titleAvatar"
          :src="
            getAssetsUrlForCDN(
              '/assets/images/perfil-oficial/esportes-pagina.png',
            )
          "
        />
      </div>
    </div>

    <div :class="$style.gradientBar">
      <div :class="[$style.teams, $style.teamsLeft]">
        <NuxtImg
          v-for="src in teamLogosLeft"
          :key="src"
          alt=""
          :class="$style.teamLogo"
          :src="getAssetsUrlForCDN(src)"
        />
      </div>

      <div :class="$style.inputFrame">
        <div :class="$style.inputInner">
          <input
            v-model="promocode"
            :class="$style.input"
            maxlength="40"
            placeholder="Digite seu código promocional"
            type="text"
            @keyup.enter="applyPromocode"
          />
          <button
            class="disabled:opacity-50 disabled:cursor-not-allowed"
            :class="$style.cta"
            :disabled="!isPromocodeButtonActive"
            type="button"
            @click="applyPromocode"
          >
            <NuxtIcon
              v-if="promocodeStore.isRedeeming"
              class="animate-spin"
              name="loading"
              style="width: 12px; height: 12px"
            />
            RESGATAR
          </button>
        </div>
      </div>

      <div :class="[$style.teams, $style.teamsRight]">
        <NuxtImg
          v-for="src in teamLogosRight"
          :key="src"
          alt=""
          :class="$style.teamLogo"
          :src="getAssetsUrlForCDN(src)"
        />
      </div>
    </div>
  </section>
</template>

<style module>
.wrapper {
  /* Imagem da faixa (definida via inline-style :style do <section>) */
  --band-image: none;
  /* Filtros da imagem (default 1 = sem alteração; >1 = mais contraste/saturação/brilho) */
  --band-image-contrast: 1.4;
  --band-image-saturation: 1.2;
  --band-image-brightness: 1.5;
  /* Tamanho da imagem (cover, contain, 100% 100%, 80% auto, etc) */
  --band-image-size: auto;
  /* Cores do gradiente vertical da faixa (mesmo do trapézio do título) */
  --band-gradient-top: #25257a;
  --band-gradient-bottom: var(--bg-bg-primary);

  /* Largura do trapézio (notch central com avatares + título) */
  --notch-width: 500px;
  /* Altura do trapézio (parte com inclinação) */
  --notch-height: 40px;
  /* Largura da barra com input + logos */
  --bar-width: 100%;
  /* Altura da faixa retangular abaixo do trapézio */
  --band-height: 76px;
  /* Quanto a faixa lateral escapa do padding do .content pai em cada lado (só no mobile) */
  --band-overflow: 0px;
  /* Inclinação das laterais do trapézio (0 = vertical, 0.0635 = padrão SVG) */
  --notch-slope: 0.0635;
  /* Raio do círculo do canto arredondado (equivalente a border-radius) */
  --notch-corner-radius: 6px;

  position: relative;
  width: 100%;
  margin: 12px auto;
  padding-bottom: 8px;
  box-sizing: border-box;
  user-select: none;
  -webkit-user-select: none;
  -moz-user-select: none;
  -ms-user-select: none;
}

.wrapper *,
.wrapper *::before,
.wrapper *::after {
  box-sizing: border-box;
}

.wrapper img {
  -webkit-user-drag: none;
  -khtml-user-drag: none;
  pointer-events: none;
}

.titlePill {
  position: relative;
  z-index: 3;
  display: inline-block;
  width: var(--notch-width);
  max-width: 100%;
  height: var(--notch-height);
  left: 50%;
  transform: translateX(-50%);
}

.clipSvg {
  position: absolute;
  width: 0;
  height: 0;
  overflow: hidden;
  pointer-events: none;
}

.bandGradient {
  position: absolute;
  top: 0;
  bottom: 0;
  left: calc(var(--band-overflow) * -1);
  right: calc(var(--band-overflow) * -1);
  background: linear-gradient(
    180deg,
    var(--band-gradient-top) 0%,
    var(--band-gradient-bottom) 100%
  );
  z-index: 0;
  pointer-events: none;
}

.bandImage {
  position: absolute;
  top: 0;
  bottom: 0;
  left: calc(var(--band-overflow) * -1);
  right: calc(var(--band-overflow) * -1);
  background: var(--band-image) center / var(--band-image-size) no-repeat;
  filter: contrast(var(--band-image-contrast, 1))
    saturate(var(--band-image-saturation, 1))
    brightness(var(--band-image-brightness, 1));
  z-index: 2;
  pointer-events: none;
}

.titlePillContent {
  position: relative;
  z-index: 3;
  display: flex;
  align-items: flex-start;
  justify-content: center;
  gap: 36px;
  height: 100%;
  padding: 8px 56px 0;
}

.titleImage {
  position: relative;
  z-index: 4;
  height: 40px;
  width: auto;
  object-fit: contain;
  flex-shrink: 0;
}

.titleAvatar {
  position: relative;
  z-index: 4;
  height: 40px;
  width: auto;
  object-fit: contain;
  flex-shrink: 0;
}

.gradientBar {
  position: relative;
  z-index: 3;
  margin-top: 0;
  display: flex;
  align-items: center;
  justify-content: center;
  gap: 25px;
  padding: 0;
  width: 100%;
  max-width: var(--bar-width);
  margin-left: auto;
  margin-right: auto;
  border-radius: 6px;
}

.teams {
  display: flex;
  align-items: center;
  gap: 25px;
  flex-shrink: 0;
}

.teamLogo {
  width: 32px;
  height: 32px;
  object-fit: contain;
  filter: drop-shadow(0 2px 4px rgba(0, 0, 0, 0.6));
}

.inputFrame {
  position: relative;
  display: flex;
  align-items: center;
  justify-content: center;
  padding: 6px;
  border-radius: 6px;
  border: 0.599px solid rgba(253, 254, 255, 0.11);
  flex: 0 1 320px;
}

.inputInner {
  display: flex;
  align-items: center;
  gap: 6px;
  width: 100%;
}

.input {
  display: flex;
  height: 30px;
  padding: 0 12px;
  align-items: center;
  gap: 5.989px;
  flex: 1 0 0;
  border-radius: 5.989px;
  border: 0.5px solid rgba(255, 255, 255, 0.6);
  background: rgba(255, 255, 255, 0.17);
  color: #fff;
  font-family: Montserrat, system-ui, sans-serif;
  font-size: 11px;
  font-weight: 500;
  outline: none;
  min-width: 0;
}

.input::placeholder {
  color: #fff;
  opacity: 0.85;
  font-weight: 500;
}

.input:focus {
  border-color: rgba(255, 255, 255, 0.85);
}

.cta {
  display: flex;
  width: 77.263px;
  height: 30px;
  padding: 0 5.989px;
  justify-content: center;
  align-items: center;
  gap: 4.193px;
  border-radius: 3.594px;
  background: #303747;
  color: #b7b7b7;
  text-align: center;
  font-family: 'Plus Jakarta Sans', system-ui, sans-serif;
  font-size: 8.385px;
  font-style: normal;
  font-weight: 800;
  line-height: 7.187px;
  border: none;
  cursor: pointer;
  white-space: nowrap;
  transition: background 0.15s ease;
  flex-shrink: 0;
}

.cta:hover:not(:disabled) {
  background: #3d4658;
  color: #fff;
}

@media (min-width: 1441px) {
  .wrapper {
    --band-image-size: 100% 100%;
  }
}

@media (max-width: 768px) {
  .wrapper {
    --notch-width: 265px;
    --notch-height: 25px;
    --notch-slope: 0.04;
    --notch-corner-radius: 4px;
    --band-height: 48px;
    --band-overflow: 16px;
    --band-image-size: 170% 180%;
    margin: 0 auto;
    padding: 0;
  }

  .bandGradient {
    opacity: 0.9;
  }

  .titlePill {
    display: block;
  }

  .titlePillContent {
    gap: 11px;
    padding: 6px 24px 0;
  }

  .titleAvatar {
    height: 19px;
  }

  .titleImage {
    height: 24px;
  }

  .gradientBar {
    margin-top: 8px;
    padding: 4px 0 0;
    gap: 6px;
    justify-content: center;
  }

  .teams {
    gap: 4px;
  }

  .teamLogo {
    width: 18px;
    height: 18px;
  }

  /* Mobile shows 3 logos each side */
  .teams .teamLogo:nth-child(n + 4) {
    display: none;
  }

  .inputFrame {
    padding: 3px;
    flex: 0 1 200px;
  }

  .inputInner {
    gap: 3px;
  }

  .input {
    height: 26px;
    padding: 0 8px;
    font-size: 9px;
  }

  .cta {
    width: auto;
    height: 26px;
    padding: 0 10px;
    font-size: 9px;
    line-height: 1;
  }
}

@media (min-width: 769px) and (max-width: 1023px) {
  .teams {
    gap: clamp(6px, 1.8vw, 25px);
  }

  .gradientBar {
    gap: clamp(8px, 2vw, 25px);
  }
}

@media (max-width: 480px) {
  .inputFrame {
    flex: 1 1 auto;
  }
}
</style>
